import React, { useContext, useEffect, useState } from 'react';
import { CircularProgress, Grid, Typography } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useMsal } from '@azure/msal-react';
import { BrandProps, ClientConfig } from '../../../../clientConfig';
import { Demographics } from '../../../../types/demographics';
import { OPENID_PARAMS } from '../../../../types/openid_enum';
import { ClientConfigContext } from '../../../clientConfigContext';
import { useValidatedUrl } from '../../../hooks/useValidatedUrl';
import { linkWithDemographics } from '../../../services/pims/pimsClient';
import getAccessToken from '../../../util/getAccessToken';
import { redirectWithSoftLogout } from '../../../util/sessionTimeout';
import BrandedCard from '../../containers/BrandedCard/BrandedCard';
import Button from '../../elements/Button';
import withLogger, { ComponentLoggerProps } from '../../HOC/WithLogger';
import { AuthenticatedRoutes } from '../../routers';
import { GeneralFormProps } from '../types';
import CollectDemographicsForm from './CollectDemographicsForm';

const errorPageRoute = AuthenticatedRoutes.LinkWithDemographicsRoutes.ContactCustomerService;

function LinkWithDemographics({ brandInfo, log }: BrandProps & ComponentLoggerProps) {
    const search = document.location.search;
    const searchParams = new URLSearchParams(search);
    const system = searchParams.get(OPENID_PARAMS.EHR_SYSTEM) || (searchParams.get(OPENID_PARAMS.SYSTEM) as string);
    const locationState = searchParams.get(OPENID_PARAMS.LOCATION_STATE);
    const redirectUrl = searchParams.get(OPENID_PARAMS.REDIRECT_URI) || (searchParams.get(OPENID_PARAMS.REDIRECT_URL) as string);
    const cancelUrl = searchParams.get(OPENID_PARAMS.CANCEL_REDIRECT_URI);

    const { instance, accounts } = useMsal();
    const [loading, setLoading] = useState(false);
    const history = useHistory();
    const { t } = useTranslation();
    const { handleSubmit, errors, control, formState } = useForm({ mode: 'onChange' });
    const clientConfiguration = useContext<ClientConfig>(ClientConfigContext);
    const validateRedirectUrl = useValidatedUrl(() => {
        log.warn('Invalid redirect_uri provided');
        history.push(errorPageRoute);
    });

    const callCancelUrl = () => {
        if (cancelUrl) {
            redirectWithSoftLogout(instance, cancelUrl, validateRedirectUrl);
        }
    };

    const validateInputParameters = () => {
        if (!system && !locationState) {
            log.warn('Missing system or locationState query parameter');
            history.push(errorPageRoute);
        } else if (!redirectUrl) {
            log.warn(`Missing redirect_uri`);
            history.push(errorPageRoute);
        } else {
            void validateRedirectUrl(redirectUrl);
        }
    };

    const onSubmit = async (data: any) => {
        const demographics: Demographics = {
            firstName: data.firstName,
            lastName: data.lastName,
            gender: data.gender,
            dateOfBirth: data.birthdate,
            phoneNumber: data.cellPhoneNumber,
            streetAddress: data.streetAddress,
            city: data.city,
            state: data.state,
            postalCode: data.zipCode,
        };
        if (!system) {
            demographics.locationState = locationState;
        }
        if (data.ssn) {
            demographics.last4ssn = data.ssn;
        }
        setLoading(true);
        try {
            const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes, log);
            const response = await linkWithDemographics({
                jwt,
                demographics,
                pimsApiUrl: clientConfiguration.pimsApiUrl,
                system,
            });
            if (response.ok) {
                const validatedRedirectUrl = await validateRedirectUrl(redirectUrl);
                if (validatedRedirectUrl) {
                    window.location.replace(validatedRedirectUrl);
                } else {
                    setLoading(false);
                }
            } else {
                setLoading(false);
                log.error(`LinkWithDemographics failed. Error occurred - ${response.status}${response.statusText ? ` -  ${response.statusText}` : ''}`);
                history.push(errorPageRoute);
            }
        } catch (err: any) {
            setLoading(false);
            log.error(`LinkWithDemographics failed. Error occurred - ${err.message}`);
            history.push(errorPageRoute);
        }
    };

    const { isDirty, isValid } = formState;

    useEffect(() => {
        window.scrollTo(0, 0);
        validateInputParameters();
    }, []);

    return (
        <>
            <div>
                <form onSubmit={handleSubmit(onSubmit)} className='displayFlex'>
                    <LinkWithDemographicsCard
                        brandInfo={brandInfo}
                        control={control}
                        errors={errors}
                        loading={loading}
                        callCancelUrl={callCancelUrl}
                        cancelUrl={cancelUrl as string}
                        t={t}
                        isDirty={isDirty}
                        isValid={isValid}
                    />
                </form>
            </div>
        </>
    );
}

export function LinkWithDemographicsCard({
    brandInfo,
    control,
    errors,
    loading,
    callCancelUrl,
    cancelUrl,
    t,
    isDirty,
    isValid,
}: BrandProps &
    GeneralFormProps & {
        loading: boolean;
        callCancelUrl: (onSubmit: any) => void;
        cancelUrl: string;
        t: any;
        isDirty: boolean;
        isValid: boolean;
    }) {
    const renderCancelButton = () => {
        if (cancelUrl) {
            return (
                <Button variant='text' type='button' onClick={callCancelUrl}>
                    {t('Cancel')}
                </Button>
            );
        }
    };

    const getHeader = () => {
        if (loading) {
            return <Typography variant='h2'>{t('Creating your account')}</Typography>;
        } else {
            return <Typography variant='h2'>{t('Help us find your medical record')}</Typography>;
        }
    };

    const getActions = () => {
        if (loading) {
            return <></>;
        } else {
            return (
                <Grid container justify='space-around' item xs={12}>
                    <Grid className='mh144' container justify='center' item xs={3}>
                        {renderCancelButton()}
                    </Grid>
                    <Grid className='mh144' container justify='center' item xs={3}>
                        <Button disabled={!isDirty || !isValid} type='submit' variant='contained'>
                            {t('Next')}
                        </Button>
                    </Grid>
                </Grid>
            );
        }
    };
    const content = loading ? (
        <CircularProgress />
    ) : (
        <div className='loadedContent'>
            <CollectDemographicsForm control={control} errors={errors} infoComponent={''} />
        </div>
    );

    return (
        <BrandedCard maxWidth={loading ? 512 : 711} brand={brandInfo}>
            {{
                header: getHeader(),
                content,
                actions: getActions(),
            }}
        </BrandedCard>
    );
}

export default withLogger(LinkWithDemographics, { eventSource: 'LinkWithDemographics' });
